import { useState, useRef, Fragment } from 'react'
import { useHistory } from 'react-router-dom'
import {
    useQuery,
    useIsSmallScreenFalse,
    getFullPathFromUrl,
} from 'packages/core'
import { useUiActionsByResource } from 'hooks'
import { EidWorkflow, Dropdown, Loader } from 'packages/eid-ui'
import config from 'config'
import { MenuItem, Button, Backdrop, styled, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Icon } from 'packages/eid-icons'

const StyledButton = styled(Button)(({ dropdownOpen, disabled }) => ({
    position: 'relative',
    height: '31px',
    padding: '0px 0px 0px 0px',
    borderRadius: '4px',
    minWidth: '32px',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.1)',

    textTransform: 'capitalize',

    backgroundColor: dropdownOpen
        ? '#3f3f55'
        : disabled
        ? '#efeff1 !important'
        : 'transparent',
    border: 'solid 1px #d8d8dd',

    zIndex: dropdownOpen ? '1202' : '0',
    '&:hover': {
        backgroundColor: '#3f3f55',
        color: 'white',
        cursor: 'hand',
        boxShadow: disabled ? 'none' : 'initial',
    },
}))

const DropdownAnchor = styled('span')({
    position: 'absolute',
    top: '40px',
    left: '50px',
})

const NoActionMessage = styled(Typography)({
    padding: '0 10px',
})

const StyledBackdrop = styled(Backdrop)(() => ({
    zIndex: '1201',
}))

export const UIActionsGearButton = ({
    item,
    noun = '',
    verb = '',
    secondVerb = '',
    resourcePath = 'applications',
}) => {
    const disabled = item.isExpired
    const iconColor = disabled ? '#8b909a' : '#3f3f55'

    const [actionsButtonIconColor, setActionsButtonIconColor] =
        useState(iconColor)
    const [openMenu, setOpenMenu] = useState(false)

    const handleOpenMenu = () => {
        setOpenMenu(true)
        setActionsButtonIconColor('white')
    }

    const anchorRef = useRef(null)

    const handleCloseMenu = () => {
        setOpenMenu(false)
        setActionsButtonIconColor(iconColor)
    }

    return (
        <Fragment>
            <StyledButton
                onClick={handleOpenMenu}
                onMouseEnter={() => {
                    setActionsButtonIconColor('white')
                }}
                onMouseLeave={() => {
                    !Boolean(openMenu) && setActionsButtonIconColor(iconColor)
                }}
                disabled={disabled}
                dropdownOpen={openMenu}
            >
                <Icon name="Actions" color={actionsButtonIconColor} />
                <DropdownAnchor ref={anchorRef} />
            </StyledButton>
            {Boolean(openMenu) && (
                <UIActionsGearMenu
                    closeMenu={handleCloseMenu}
                    noun={noun}
                    verb={verb}
                    secondVerb={secondVerb}
                    resourcePath={resourcePath}
                    anchorEl={openMenu}
                    anchorRef={anchorRef}
                    item={item}
                />
            )}
        </Fragment>
    )
}

const UIActionsGearMenu = ({
    item,
    noun = '',
    verb = '',
    secondVerb = '',
    resourcePath = 'applications',
    closeMenu,
    anchorRef,
}) => {
    const query = useQuery()
    const history = useHistory()
    const { t } = useTranslation()
    const isSmallScreen = useIsSmallScreenFalse()
    const UI_ACTIONS_NOUN = noun
    const UI_ACTIONS_VERB = verb

    const { data, isLoading } = useUiActionsByResource(
        item.id,
        UI_ACTIONS_NOUN,
        UI_ACTIONS_VERB,
    )

    const { data: otherActions, isFetching: otherActionsLoading } =
        useUiActionsByResource(
            item.id,
            `${UI_ACTIONS_NOUN}`,
            secondVerb,
            Boolean(secondVerb),
        )

    const onActionClicked = (workflowName) => {
        query.set('workflowName', workflowName)
        query.set('returnUrl', getFullPathFromUrl())
        query.set('SelectedResourceID', item.resourceId)
        history.push(
            `${
                config.APP_SUBPATH
            }/${resourcePath}/workflows?${query.toString()}`,
        )
    }

    return (
        <>
            <StyledBackdrop
                sx={{ color: '#fff' }}
                open={true}
                onClick={closeMenu}
            />
            <Dropdown
                anchorEl={anchorRef.current}
                showCone={!isSmallScreen && !secondVerb}
                keepMounted
                open={true}
                onClose={closeMenu}
                width={'auto'}
                listStyles={{
                    paddingBottom: '0px',
                    whiteSpace: 'normal',
                    '& > ul': {
                        paddingBottom: '7px',
                        paddingTop: '0px',
                    },
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                iconPosition={{
                    top: '-8px',
                    right: '13px',
                }}
                disableScrollLock={false}
            >
                {isLoading || otherActionsLoading ? (
                    <Loader height={40} />
                ) : (!isLoading && data && data.length > 0) ||
                  (!otherActionsLoading &&
                      otherActions &&
                      otherActions.length > 0) ? (
                    <>
                        {data?.length > 0 &&
                            data.map((d) =>
                                isSmallScreen ? (
                                    <EidWorkflow
                                        key={d.id}
                                        baseEidUrl={config.BASE_EID_URL}
                                        title={d.friendlyName}
                                        workflowName={d.requestWorkflowName}
                                        workflowParams={[
                                            `SelectedResourceID=${item.resourceId}`,
                                        ]}
                                    >
                                        <MenuItem
                                            onClick={closeMenu}
                                            style={{ whiteSpace: 'normal' }}
                                        >
                                            {d.friendlyName}
                                        </MenuItem>
                                    </EidWorkflow>
                                ) : (
                                    <MenuItem
                                        key={d.id}
                                        onClick={() => {
                                            closeMenu()
                                            onActionClicked(
                                                d.requestWorkflowName,
                                            )
                                        }}
                                    >
                                        {d.friendlyName}
                                    </MenuItem>
                                ),
                            )}
                        {otherActions?.length > 0 &&
                            otherActions.map((d) =>
                                isSmallScreen ? (
                                    <EidWorkflow
                                        key={d.id}
                                        baseEidUrl={config.BASE_EID_URL}
                                        title={d.friendlyName}
                                        workflowName={d.requestWorkflowName}
                                        workflowParams={[
                                            `SelectedResourceID=${item.resourceId}`,
                                        ]}
                                    >
                                        <MenuItem
                                            onClick={closeMenu}
                                            style={{ whiteSpace: 'normal' }}
                                        >
                                            {d.friendlyName}
                                        </MenuItem>
                                    </EidWorkflow>
                                ) : (
                                    <MenuItem
                                        key={d.id}
                                        onClick={() => {
                                            closeMenu()
                                            onActionClicked(
                                                d.requestWorkflowName,
                                            )
                                        }}
                                    >
                                        {d.friendlyName}
                                    </MenuItem>
                                ),
                            )}
                    </>
                ) : (
                    <MenuItem disabled>
                        <NoActionMessage>{t('NoActionFound')}</NoActionMessage>
                    </MenuItem>
                )}
            </Dropdown>
        </>
    )
}
